import { useMemo } from 'react';

import type { AspectRatio } from '@uncinc/react-drupal-core/dist/Components/DynamicMediaItem';

import useWindowDimensions from './useWindowDimensions';

import type { LayoutVariables } from '../types';
import {
  LAYOUT_VARIABLES,
  SCREEN_LG,
  SCREEN_MD,
  SCREEN_SM,
  SCREEN_XL,
} from '../config/constants';

type RestLayoutVariables<K extends keyof LayoutVariables> = Omit<LayoutVariables[K],
| 'heroImageRatioXS'
| 'heroImageRatioSM'
| 'heroImageRatioMD'
| 'heroImageRatioLG'
| 'heroImageRatioXL'
>;

const heroImageAspectRatios = [
  'heroImageRatioXS',
  'heroImageRatioSM',
  'heroImageRatioMD',
  'heroImageRatioLG',
  'heroImageRatioXL',
];

export type AspectRatiosList = [AspectRatio, AspectRatio, AspectRatio, AspectRatio, AspectRatio];

/**
 * A hook to get the aspect ratio from a layout variable for the current breakpoint.
 * Use this instead of useLayoutVariables if you have multiple images with variable
 * aspect ratios.
 *
 * @param layoutVariablesKey
 * to determine for which key to get the layout variables from
 * @param aspectRatioKeys
 * a set of aspect ratio keys, one for each breakpoint (XS, SM, MD, LG, XL)
 *
 * @returns
 * aspect ratio for the current breakpoint
 */
export const useVariableImageAspectRatio = <K extends keyof LayoutVariables, S extends keyof LayoutVariables[K]>(
  layoutVariablesKey: K,
  aspectRatioKeys: [S, S, S, S, S],
): AspectRatio => {
  const { width } = useWindowDimensions();

  const layoutVariables = LAYOUT_VARIABLES[layoutVariablesKey] as Record<S, AspectRatio>;

  const [
    cropXS,
    cropSM,
    cropMD,
    cropLG,
    cropXL,
  ] = aspectRatioKeys;

  return useMemo(() => {
    if (
      cropXS in layoutVariables &&
      width < SCREEN_SM
    ) {
      return layoutVariables[cropXS];
    }

    if (
      cropSM in layoutVariables &&
      width >= SCREEN_SM &&
      width < SCREEN_MD
    ) {
      return layoutVariables[cropSM];
    }

    if (
      cropMD in layoutVariables &&
      width >= SCREEN_MD &&
      width < SCREEN_LG
    ) {
      return layoutVariables[cropMD];
    }

    if (
      cropLG in layoutVariables &&
      width >= SCREEN_LG &&
      width < SCREEN_XL
    ) {
      return layoutVariables[cropLG];
    }

    if (
      cropXL in layoutVariables &&
      width >= SCREEN_XL
    ) {
      return layoutVariables[cropXL];
    }

    return 'original';
  }, [cropLG, cropMD, cropSM, cropXL, cropXS, layoutVariables, width]);
};

const useLayoutVariables = <K extends keyof LayoutVariables>(key: K) => {
  const layoutVariables = LAYOUT_VARIABLES[key];

  const restLayoutVariables = Object.fromEntries(
    Object.entries(layoutVariables).filter(([variableKey]) => !heroImageAspectRatios.includes(variableKey)),
  ) as RestLayoutVariables<K>;

  const heroImageAspectRatio = useVariableImageAspectRatio(
    key,
    [
      'heroImageRatioXS',
      'heroImageRatioSM',
      'heroImageRatioMD',
      'heroImageRatioLG',
      'heroImageRatioXL',
    ] as [
      keyof LayoutVariables[K],
      keyof LayoutVariables[K],
      keyof LayoutVariables[K],
      keyof LayoutVariables[K],
      keyof LayoutVariables[K],
    ],
  );

  const heroImageBreakpointAspectRatios: AspectRatiosList = useMemo(() => ([
    'heroImageRatioXS' in layoutVariables ? layoutVariables.heroImageRatioXS : 'original',
    'heroImageRatioSM' in layoutVariables ? layoutVariables.heroImageRatioSM : 'original',
    'heroImageRatioMD' in layoutVariables ? layoutVariables.heroImageRatioMD : 'original',
    'heroImageRatioLG' in layoutVariables ? layoutVariables.heroImageRatioLG : 'original',
    'heroImageRatioXL' in layoutVariables ? layoutVariables.heroImageRatioXL : 'original',
  ]), [layoutVariables]);

  return {
    heroImageAspectRatio,
    heroImageBreakpointAspectRatios,
    ...restLayoutVariables,
  };
};

export default useLayoutVariables;
